/**
 * Banner under the interviewer select that flags picked interviewers
 * exceeding weekly workload or being unavailable for the chosen slot.
 * Ported from KInterviewScheduler SchInterviewersInfo.tsx (lines 32-67).
 *
 * Consumes `interviewer_status` from the interview plan response:
 *   { [interviewerId]: { has_exceeded_max_workload, is_unavailable } }
 */
import { Flex, Icon, Text } from '@chakra-ui/react';
import { PiSmileySadDuotone } from 'react-icons/pi';

export interface InterviewerStatusEntry {
  has_exceeded_max_workload?: boolean;
  is_unavailable?: boolean;
}

export type InterviewerStatusMap = Record<
  string | number,
  InterviewerStatusEntry
>;

interface InterviewerStatusBannerProps {
  selectedIds: Array<string | number>;
  status: InterviewerStatusMap | undefined;
}

export const InterviewerStatusBanner = ({
  selectedIds,
  status,
}: InterviewerStatusBannerProps) => {
  if (!status || selectedIds.length === 0) return null;

  const loadExceededCount = selectedIds.filter(
    (id) => status[id]?.has_exceeded_max_workload
  ).length;
  const unavailableCount = selectedIds.filter(
    (id) => status[id]?.is_unavailable
  ).length;

  if (loadExceededCount === 0 && unavailableCount === 0) return null;

  const title =
    loadExceededCount > 0
      ? loadExceededCount > 1
        ? "Interviewers' weekly interview load exceeded"
        : "Interviewer's weekly interview load exceeded"
      : unavailableCount > 1
        ? 'Interviewers are not available'
        : 'Interviewer is not available';

  const description =
    loadExceededCount > 0
      ? loadExceededCount > 1
        ? 'Remove them, pick other interviewers or schedule it on a different week'
        : 'Pick another interviewer or schedule it on a different week'
      : unavailableCount > 1
        ? 'Remove them, pick other interviewers or schedule it on a different day or time'
        : 'Pick another interviewer or schedule it on a different day or time';

  return (
    <Flex
      align="flex-start"
      gap="2"
      mt="2"
      bg="secondary-50"
      borderRadius="md"
      p="3"
    >
      <Icon
        as={PiSmileySadDuotone}
        boxSize="5"
        color="var(--kula-colors-secondary-700)"
      />
      <Flex flexDir="column" gap="1">
        <Text fontSize="sm" fontWeight="medium" color="secondary-900">
          {title}
        </Text>
        <Text fontSize="sm" color="secondary-700">
          {description}
        </Text>
      </Flex>
    </Flex>
  );
};
